import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import type { RootState } from '../../../store/store';
import classNames from '../../../utils/classNames';
import { ROUTES } from '../../../utils/routes';

const ROLE_ITEMS = [
  'Assurer la passerelle entre le syndic et les services transaction / gestion locative',
  'Recueillir le consentement préalable des copropriétaires',
  'Coordonner les envois de communication RGPD conformes',
  'Suivre les actions planifiées sur 12 mois',
];

const MISSION_ITEMS = [
  'Être l\u2019interlocuteur identifié par les copropriétaires pour leurs projets immobiliers',
  'Générer des opportunités de mandat en restant dans le cadre légal',
  'Éviter tout détournement de finalité des données de copropriété',
  'Agir uniquement comme interface \u2014 jamais en démarchage direct',
];

function PrealableSection() {
  const [isOpen, setIsOpen] = useState(false);
  const isAuthenticated = useSelector((state: RootState) => !!state.auth.company?.id);

  return (
    <div className="overflow-hidden rounded-[10px] border border-l-[4px] border-gray-200 border-l-rgpd-blue bg-gray-50">
      {/* Banner row */}
      <div className="flex items-center justify-between gap-5 px-6 py-4">
        <div className="flex min-w-0 flex-1 items-center gap-3.5">
          <span className="shrink-0">
            <UserIcon className="h-6 w-6 text-rgpd-blue" />
          </span>

          <div className="min-w-0 flex-1">
            <h2 className="mb-0.5 text-sm font-bold text-slate-900">
              Préalable : Nommer un &quot;Référent Mutation Location&quot; au sein du cabinet
            </h2>
            <p className="text-[13px] leading-relaxed text-gray-800">
              Pourquoi désigner un référent mutation-location est-il nécessaire ? Ce rôle permet d&apos;assurer une
              passerelle claire entre le syndic, dans sa mission de conseil, et les autres services (transaction, gestion
              locative), tout en respectant le cadre légal. Le référent n&apos;a pas une démarche commerciale : il agit
              uniquement comme interface. Il met en relation le copropriétaire avec les services concernés, uniquement
              après avoir recueilli son consentement préalable lors de l&apos;échange. Ce fonctionnement permet ainsi
              d&apos;éviter tout risque de détournement de finalité.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className={classNames(
            'flex shrink-0 items-center gap-1.5 rounded-[7px] px-4 py-2 text-xs font-bold whitespace-nowrap text-white transition-colors',
            isOpen ? 'bg-rgpd-blue-dark' : 'bg-rgpd-blue hover:bg-rgpd-blue-dark',
          )}
        >
          Rôle et missions
          <ChevronDownIcon
            className={classNames('h-3.5 w-3.5 transition-transform duration-200', isOpen && 'rotate-180')}
          />
        </button>
      </div>

      {/* Expandable detail panel */}
      {isOpen && (
        <div className="border-t border-gray-200">
          {/* Blue gradient header */}
          <div className="flex items-center gap-3 bg-gradient-to-r from-rgpd-blue-dark to-rgpd-blue px-5 py-3.5">
            <UserIcon className="h-5 w-5 shrink-0 text-white" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-extrabold text-white">Référent Mutation Location</div>
              <div className="text-[11px] text-white/75">
                Interface entre la gestion de la copropriété et les services commerciaux du cabinet
              </div>
            </div>
            {isAuthenticated && (
              <Link
                to={ROUTES.COMMUNICATION_REFERENT_FORMATION}
                className="flex shrink-0 items-center gap-1.5 rounded-[7px] border border-white/30 bg-white/[.18] px-4 py-1.5 text-[11px] font-bold whitespace-nowrap text-white transition-colors hover:bg-white/30"
              >
                Rôle et missions complet
                <ChevronRightIcon className="h-3 w-3" />
              </Link>
            )}
          </div>

          {/* Two-column grid: Son rôle / Sa mission */}
          <div className="grid grid-cols-2 gap-4 bg-white px-5 py-4">
            <div>
              <div className="mb-2 text-[11px] font-extrabold tracking-wide text-green-600 uppercase">
                <span role="img" aria-label="Cible">🎯</span> Son rôle
              </div>
              <ul className="flex flex-col gap-1 pl-4 text-[12.5px] leading-relaxed text-gray-700">
                {ROLE_ITEMS.map((item) => (
                  <li key={item} className="list-disc">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="mb-2 text-[11px] font-extrabold tracking-wide text-rgpd-blue uppercase">
                <span role="img" aria-label="Presse-papier">📋</span> Sa mission
              </div>
              <ul className="flex flex-col gap-1 pl-4 text-[12.5px] leading-relaxed text-gray-700">
                {MISSION_ITEMS.map((item) => (
                  <li key={item} className="list-disc">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Warning banner */}
          <div className="px-5 pb-4">
            <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3.5 py-2.5 text-xs leading-relaxed text-orange-800">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
              <p>
                <strong>Important :</strong> Le référent agit uniquement comme interface, après recueil du consentement
                préalable — évitant tout risque de détournement de finalité passible de{' '}
                <strong>5 ans d&apos;emprisonnement et 300 000 € d&apos;amende</strong>.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PrealableSection;
